/**
 * SplitChart — composition wrapper over Recharts (P0 kit). `variant="donut"`
 * (default) renders a Pie donut with an optional center label/value;
 * `variant="stacked"` renders a single horizontal stacked bar (a token-styled
 * div stack — lighter than a 1-row BarChart). Both always render a legend that
 * lists each slice with a color swatch + value, so the split is never conveyed
 * by color alone. Colors cycle CHART_SERIES unless a slice sets `colorVar`.
 * Design: _shared/KIT-SPEC.md.
 */
import { type ReactElement } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { AdminStates } from "@/features/admin/_shared/AdminStates";
import { CHART, CHART_SERIES } from "@/features/admin/_shared/chartTokens";

export interface SplitSlice {
  label: string;
  value: number;
  colorVar?: string;
}
export interface SplitChartProps {
  data: readonly SplitSlice[];
  variant?: "donut" | "stacked";
  height?: number;
  isLoading?: boolean;
  isError?: boolean;
  loadingLabel: string;
  emptyLabel: string;
  errorLabel: string;
  centerLabel?: string;
  centerValue?: string;
  ariaLabel: string;
  testid?: string;
}

function colorFor(slice: SplitSlice, i: number): string {
  return slice.colorVar ?? CHART_SERIES[i % CHART_SERIES.length] ?? CHART.primary;
}

export function SplitChart({
  data,
  variant = "donut",
  height = 220,
  isLoading = false,
  isError = false,
  loadingLabel,
  emptyLabel,
  errorLabel,
  centerLabel,
  centerValue,
  ariaLabel,
  testid = "split-chart",
}: SplitChartProps): ReactElement {
  const total = data.reduce((sum, s) => sum + s.value, 0);
  const tooltipStyle = {
    background: CHART.surface,
    border: `1px solid ${CHART.grid}`,
    borderRadius: 10,
    color: CHART.ink,
    fontSize: 12,
  } as const;
  // Recharts' DefaultTooltipContent colors each item line with
  // `entry.color || '#000'` when no `itemStyle` is supplied. `Pie` here
  // carries no chart-level `fill` (each `Cell` sets its own), so
  // `entry.color` is undefined and Recharts falls back to hardcoded black —
  // invisible against the dark-theme tooltip background (SCRUM-150).
  // `contentStyle.color` only reaches the *label* line via CSS inheritance,
  // not the value line, so it must be set here too.
  const tooltipItemStyle = { color: CHART.ink } as const;

  const legend = (
    <ul data-testid={`${testid}-legend`} className="flex flex-wrap gap-x-4 gap-y-1.5">
      {data.map((s, i) => (
        <li key={s.label} className="flex items-center gap-2 text-xs text-[var(--g-secondary)]">
          <span aria-hidden className="size-2.5 rounded-full" style={{ backgroundColor: colorFor(s, i) }} />
          <span className="text-foreground">{s.label}</span>
          <span className="tabular-nums">{s.value}</span>
        </li>
      ))}
    </ul>
  );

  return (
    <AdminStates
      isLoading={isLoading}
      isError={isError}
      isEmpty={data.length === 0}
      loadingLabel={loadingLabel}
      emptyLabel={emptyLabel}
      errorLabel={errorLabel}
      testid={testid}
    >
      <div data-testid={testid} role="img" aria-label={ariaLabel} className="flex flex-col gap-3">
        {variant === "donut" ? (
          <div className="relative" style={{ width: "100%", height }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data as SplitSlice[]}
                  dataKey="value"
                  nameKey="label"
                  innerRadius="60%"
                  outerRadius="85%"
                  paddingAngle={2}
                  stroke={CHART.surface}
                  isAnimationActive={false}
                >
                  {data.map((s, i) => (
                    <Cell key={s.label} fill={colorFor(s, i)} />
                  ))}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} itemStyle={tooltipItemStyle} />
              </PieChart>
            </ResponsiveContainer>
            {centerValue ? (
              <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-xl font-bold tabular-nums text-foreground">{centerValue}</span>
                {centerLabel ? (
                  <span className="text-[10.5px] uppercase tracking-[0.04em] text-[var(--g-secondary)]">{centerLabel}</span>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : (
          <div data-testid={`${testid}-stacked`} className="flex h-3 w-full overflow-hidden rounded-full bg-[var(--g-hairline)]">
            {data.map((s, i) => (
              <div
                key={s.label}
                className="h-full"
                style={{
                  width: total > 0 ? `${(s.value / total) * 100}%` : "0%",
                  backgroundColor: colorFor(s, i),
                }}
              />
            ))}
          </div>
        )}
        {legend}
      </div>
    </AdminStates>
  );
}

export default SplitChart;
